import React, { useState, useEffect } from "react";
import { BackButton, Checkbox, Size, HyperLink, InputLabel, DatepickerField, Textarea, ComponentS, ModalDialog, ComponentM } from "@laerdal/life-react-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import {
    PageContainer,
    CaseContent,
    Title,
    SubTitle,
    InputRow,
    InputCell,
    CreateButton,
    LinkContainer,
    CaseForm,
    Header,
} from "./CaseFormPage.styles";

import { countryList } from "../../model/constants/CountryList";
import { caseTypeSubtypes } from "../../model/constants/CaseTypeSubtype";
import { PageWidthFull } from "../../commonComponents/PageWidthFull";

// Custom hooks
import { useCaseForm } from "./hooks/useCaseForm";
import { useFileUpload } from "./hooks/useFileUpload";
import { useCaseSubmission } from "./hooks/useCaseSubmission";
import { selectUserProfile } from "../../store/slices/userSlice";

// Components
import { FormTextField } from "./components/FormTextField";
import { FormDropdownField } from "./components/FormDropdownField";
import { FileUploadSection } from "./components/FileUploadSection";
import { LicenseRevocationModal } from "./components/LicenseRevocationModal";

// Constants
import { ORGANIZATION_TYPES, ROLE_OPTIONS, PRODUCT_OPTIONS } from "./constants/formConstants";

const CaseFormPageRefactored = () => {
    const { option, suboption } = useParams<{ option: string, suboption: string }>();
    const { t } = useTranslation("cases");
    const user = useSelector(selectUserProfile);

    const caseOption = caseTypeSubtypes.find((type) => type.value === option);
    const caseSubOption = caseOption?.subtypes.find((subtype) => subtype.value === suboption);

    // Custom hooks
    const { control, handleSubmit, formState: { errors, isValid }, watch, register } = useCaseForm(option!, suboption!, caseSubOption);
    const fileUpload = useFileUpload();
    const { creatingcase, modalOpen, setModalOpen, submitCase } = useCaseSubmission();

    // Local state
    const [privacyStatement, setPrivacyStatement] = useState<boolean>(false);
    const [llpLicenseDate, setLlpLicenseDate] = useState<Date>(new Date());
    const [internalEmailModalOpen, setInternalEmailModalOpen] = useState<boolean>(false);
    const [pendingSubmitData, setPendingSubmitData] = useState<any>(null);

    // Watch contact method for conditional validation
    const contactMethod = watch('contactMethod');

    const [isMobile, setIsMobile] = useState(() => !window.matchMedia('(min-width: 768px)').matches);
    useEffect(() => {
        const mql = window.matchMedia('(min-width: 768px)');
        const handler = (e: MediaQueryListEvent) => setIsMobile(!e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    const onSubmit = (data: any) => {
        if (data.workEmail?.toLowerCase().includes('@laerdal.')) {
            setPendingSubmitData(data);
            setInternalEmailModalOpen(true);
            return;
        }
        submitCase(data, fileUpload.files, option!, suboption!, caseSubOption, llpLicenseDate);
    };

    const handleInternalEmailProceed = () => {
        setInternalEmailModalOpen(false);
        if (pendingSubmitData) {
            submitCase(pendingSubmitData, fileUpload.files, option!, suboption!, caseSubOption, llpLicenseDate);
            setPendingSubmitData(null);
        }
    };

    const getOrganizationOptions = () => {
        return caseSubOption?.value === 'revokeAhaLicense'
            ? ORGANIZATION_TYPES.AHA_LICENSE
            : ORGANIZATION_TYPES.GENERAL;
    };

    return (
        <>
            <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
                <PageContainer>
                    <Header>
                        <BackButton size={Size.Small} onClick={() => window.history.back()}>
                            {t('Back')}
                        </BackButton>
                        <Title>{t('Can you give us more details?')}</Title>
                        <SubTitle>{t('Please provide more information so our team can assist you better')}</SubTitle>
                    </Header>

                    <CaseContent>
                        <CaseForm onSubmit={handleSubmit(onSubmit)}>
                            <input type="hidden" {...register("productLineCode")} />
                            <input type="hidden" {...register("deflectionResponse")} />
                            {/* Personal Information */}
                            <InputRow>
                                <FormTextField
                                    name="firstName"
                                    control={control}
                                    errors={errors}
                                    label={t('First name')}
                                    placeholder={t('e.g. Annie')}
                                    required={true}
                                    testId="FirstName"
                                    disabled={!!user?.firstName}
                                />
                                <FormTextField
                                    name="lastName"
                                    control={control}
                                    errors={errors}
                                    label={t('Last name')}
                                    placeholder={t('e.g. Laerdal')}
                                    required={true}
                                    testId="LastName"
                                    disabled={!!user?.lastName}
                                />
                            </InputRow>

                            <InputRow>
                                <FormTextField
                                    name="workEmail"
                                    control={control}
                                    errors={errors}
                                    label={t('Work email')}
                                    placeholder={t('e.g. annie.laerdal@laerdal.com')}
                                    required={true}
                                    testId="WorkEmail"
                                    disabled={!!user?.email}
                                />
                                <FormTextField
                                    name="telephone"
                                    control={control}
                                    errors={errors}
                                    label={t('Telephone')}
                                    placeholder={t('e.g. 987654321')}
                                    required={contactMethod === 'phone'}
                                    testId="telephone"
                                />
                            </InputRow>

                            {/* Organization and Contact Method */}
                            <InputRow>
                                <FormTextField
                                    name="organization"
                                    control={control}
                                    errors={errors}
                                    label={t('Organization')}
                                    placeholder={t('e.g. Laerdal Medical')}
                                    testId="Organization"
                                    disabled={!!user?.currentOrganization?.name}
                                />
                                <InputCell>
                                    <InputLabel inputId="contactMethod" text={t('Preferred contact method')} size={Size.Small} />
                                    <Controller
                                        name="contactMethod"
                                        control={control}
                                        render={({ field }) => (
                                            <div style={{ display: 'flex', gap: '16px' }}>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        value="email"
                                                        checked={field.value === 'email'}
                                                        onChange={() => field.onChange('email')}
                                                    />
                                                    {t('Work email')}
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        value="phone"
                                                        checked={field.value === 'phone'}
                                                        onChange={() => field.onChange('phone')}
                                                    />
                                                    {t('Telephone')}
                                                </label>
                                            </div>
                                        )}
                                    />
                                </InputCell>
                            </InputRow>

                            {/* Order and Product Information */}
                            <InputRow>
                                {caseSubOption?.orderNumberVisible && (
                                    <FormTextField
                                        name="orderOrPoNumber"
                                        control={control}
                                        errors={errors}
                                        label={t('Enter the Order and/or PO Number')}
                                        placeholder={t('e.g. P3254434')}
                                        testId="OrderOrPoNumber"
                                    />
                                )}
                                {(!caseSubOption || caseSubOption?.serialNumberVisible) && (
                                    <FormTextField
                                        name="productSerial"
                                        control={control}
                                        errors={errors}
                                        label={t('Enter product serial number(s)')}
                                        placeholder={t('e.g. TSBH02010077')}
                                        testId="ProductSerial"
                                    />
                                )}
                            </InputRow>

                            {/* Location Information */}
                            <InputRow>
                                <FormDropdownField
                                    name="country"
                                    control={control}
                                    errors={errors}
                                    label={t('Country')}
                                    required={true}
                                    testId="CountryId"
                                    options={countryList}
                                    disabled={!!user?.currentOrganization?.address?.country?.codeAlpha2}
                                />
                                <FormTextField
                                    name="stateOrProvince"
                                    control={control}
                                    errors={errors}
                                    label={t('State or Province')}
                                    testId="stateOrProvince"
                                    disabled={!!user?.currentOrganization?.address?.state}
                                />
                            </InputRow>

                            <InputRow>
                                <FormTextField
                                    name="zipCode"
                                    control={control}
                                    errors={errors}
                                    label={t('Zip code')}
                                    testId="zipCode"
                                    disabled={!!user?.currentOrganization?.address?.zipCode}
                                />
                                <FormTextField
                                    name="city"
                                    control={control}
                                    errors={errors}
                                    label={t('City')}
                                    testId="city"
                                    disabled={!!user?.currentOrganization?.address?.city}
                                />
                            </InputRow>

                            {/* Organization and Role */}
                            <InputRow>
                                <FormDropdownField
                                    name="organisationType"
                                    control={control}
                                    errors={errors}
                                    label={t('Organization type')}
                                    required={caseSubOption?.organizationTypeRequired ?? false}
                                    testId="organisationType"
                                    options={getOrganizationOptions()}
                                />
                                <FormDropdownField
                                    name="role"
                                    control={control}
                                    errors={errors}
                                    label={t('Role')}
                                    testId="role"
                                    options={ROLE_OPTIONS}
                                />
                            </InputRow>

                            {/* Special Fields for License Revocation */}
                            <InputRow>
                                {caseSubOption?.llpOrganizationIDVisible && (
                                    <FormTextField
                                        name="llpOrganizationID"
                                        control={control}
                                        errors={errors}
                                        label={t('LLP Organization ID')}
                                        required={true}
                                        testId="llpOrganizationID"
                                    />
                                )}
                                {caseSubOption?.productVisible && (
                                    <FormDropdownField
                                        name="product"
                                        control={control}
                                        errors={errors}
                                        label={t('Product')}
                                        required={true}
                                        testId="product"
                                        options={PRODUCT_OPTIONS}
                                    />
                                )}
                            </InputRow>

                            {/* License Date */}
                            {!!caseSubOption?.creationDateLicenseVisible && (
                                <>
                                    <InputLabel inputId="llpLicenseDate" text={t('Creation date of the license')} size={Size.Small} required={true} />
                                    <DatepickerField
                                        onChange={(d) => setLlpLicenseDate(d)}
                                        value={llpLicenseDate}
                                        id="llpLicenseDate"
                                        data-testId="llpLicenseDate"
                                    />
                                </>
                            )}

                            {/* Account and Product Name */}
                            <InputRow>
                                {(!caseSubOption || caseSubOption?.accountNumberVisible) && (
                                    <FormTextField
                                        name="accountNumber"
                                        control={control}
                                        errors={errors}
                                        label={t('Account number')}
                                        required={caseSubOption?.accountNumberRequired}
                                        testId="accountNumber"
                                        disabled={!!user?.currentOrganization?.customerNo}
                                    />
                                )}
                                {(!caseSubOption || caseSubOption?.productNameVisible) && (
                                    <FormTextField
                                        name="productName"
                                        control={control}
                                        errors={errors}
                                        label={t('Product name')}
                                        required={caseSubOption?.productNameRequired}
                                        testId="productName"
                                    />
                                )}
                            </InputRow>

                            {/* Return Reason */}
                            <InputRow>
                                {caseSubOption?.returnReasonVisible && (
                                    <FormTextField
                                        name="reasonForReturn"
                                        control={control}
                                        errors={errors}
                                        label={t('Reason for return')}
                                        testId="reasonForReturn"
                                    />
                                )}
                            </InputRow>

                            {/* Description */}
                            <InputRow>
                                <InputCell>
                                    <InputLabel inputId="Description" text={t('Description')} size={Size.Small} required={true} />
                                    <Controller
                                        name="description"
                                        control={control}
                                        render={({ field }) => (
                                            <Textarea
                                                {...field}
                                                id="Description"
                                                size={Size.Medium}
                                                data-testId="Description"
                                                style={{ fontFamily: 'Lato' }}
                                                validationMessage={errors.description?.message as string}
                                                placeholder={caseSubOption?.value === 'revokeAhaLicense'
                                                    ? t('Please enter your course-url. or e-book code here')
                                                    : t('Description')}
                                            />
                                        )}
                                    />
                                </InputCell>
                            </InputRow>

                            {/* File Upload */}
                            <FileUploadSection
                                files={fileUpload.files}
                                fileSizeOverLimit={fileUpload.fileSizeOverLimit}
                                fileInputRef={fileUpload.fileInputRef}
                                onDragEnter={fileUpload.handleDragEnter}
                                onDragLeave={fileUpload.handleDragLeave}
                                onDragOver={fileUpload.handleDragOver}
                                onDrop={fileUpload.handleDrop}
                                onClick={fileUpload.handleClick}
                                onButtonClick={fileUpload.handleButtonClick}
                                onFileInputChange={fileUpload.handleFileInputChange}
                                onRemoveFile={fileUpload.handleRemoveFile}
                            />

                            {/* Privacy Statement */}
                            <InputRow>
                                <Checkbox
                                    label={t('By checking this box, I acknowledge that I have read and understood the Laerdal Privacy Statement provided below. I consent to the storage, sharing, and processing of my personal information, including my name, phone number, and email address, which I have provided on this form, for the purposes outlined in the Laerdal Privacy Statement.')}
                                    selected={privacyStatement}
                                    select={(s) => setPrivacyStatement(s)}
                                />
                            </InputRow>
                            <LinkContainer>
                                <HyperLink href='https://laerdal.com/support/customer-service/privacy-policy/' variant='default'>
                                    {t('Laerdal Privacy Statement')}
                                </HyperLink>
                            </LinkContainer>

                            {/* Submit Button */}
                            <CreateButton
                                size={Size.Large}
                                disabled={fileUpload.fileSizeOverLimit || !privacyStatement || !isValid}
                                loading={creatingcase}
                                type="submit">
                                {t('Submit')}
                            </CreateButton>

                            <InputRow>
                                <ComponentS>
                                    {t('We will handle your personal contact details with care as outlined in')}
                                    &nbsp;
                                    <HyperLink variant="default" href="https://laerdal.com/support/customer-service/privacy-statement/" target="_blank">
                                        {t("Laerdal's Privacy Policy.")}
                                    </HyperLink>
                                </ComponentS>
                            </InputRow>
                        </CaseForm>
                    </CaseContent>
                </PageContainer>

                {/* Modal */}
                <LicenseRevocationModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                />

                <ModalDialog
                    title={t('Internal email detected')}
                    size={isMobile ? Size.Small : undefined}
                    isModalOpen={internalEmailModalOpen}
                    closeAction={() => setInternalEmailModalOpen(false)}
                    submitAction={handleInternalEmailProceed}
                    buttons={[
                        {
                            action: () => setInternalEmailModalOpen(false),
                            text: t('Cancel'),
                            variant: 'secondary' as any,
                        },
                        {
                            action: handleInternalEmailProceed,
                            text: t('Proceed'),
                        },
                    ]}
                >
                    <ComponentM>
                        {t('You are using an internal email, this will effect how the case is processed')}
                    </ComponentM>
                </ModalDialog>
            </PageWidthFull>
        </>
    );
};

export default CaseFormPageRefactored;
